import React from "react";

import { type UserActionPermissionBooleans } from "@src/lib/contexts/ActionPermissionsContext";
import { Ff, LocationGroupType, UserType } from "@src/types.generated";
import ArrowUpRightIcon from "@src/lib/icons/18px/arrow-up-right";
import AsteriskIcon from "@src/lib/icons/18px/asterisk";
import Archive2OutlineIcon from "@src/lib/icons/18px/archive-2-outline";
import BoltOutlineIcon from "@src/lib/icons/18px/bolt-outline";
import BoltSpeed2Icon from "@src/lib/icons/18px/bolt-speed-2";
import BookOpenOutlineIcon from "@src/lib/icons/18px/book-open-outline";
import BucketPaintOutlineIcon from "@src/lib/icons/18px/bucket-paint-outline";
import CalendarOutlineIcon from "@src/lib/icons/18px/calendar-outline";
import CoinsOutlineIcon from "@src/lib/icons/18px/coins-outline";
import CopyrightOutlineIcon from "@src/lib/icons/18px/copyright-outline";
import EnvelopeContentOutlineIcon from "@src/lib/icons/18px/envelope-content-outline";
import FileSendOutlineIcon from "@src/lib/icons/18px/file-send-outline";
import Flag3FillIcon from "@src/lib/icons/18px/flag-3-fill";
import NewspaperOutlineIcon from "@src/lib/icons/18px/newspaper-outline";
import MsgsOutlineIcon from "@src/lib/icons/18px/msgs-outline";
import ClipboardIcon from "@src/lib/icons/18px/clipboard";
import LawShieldIcon from "@src/lib/icons/18px/law-shield";
import ListCheckboxIcon from "@src/lib/icons/18px/list-checkbox";
import Tasks2Icon from "@src/lib/icons/18px/tasks-2";
import ArchiveOutlineIcon from "@src/lib/icons/18px/archive-outline";
import SlidersOutlineIcon from "@src/lib/icons/18px/sliders-outline";
import AwardCertificateOutlineIcon from "@src/lib/icons/18px/award-certificate-outline";
import FileArrowInIcon from "@src/lib/icons/18px/file-arrow-in";
import GlobeIcon from "@src/lib/icons/18px/globe";
import GridIcon from "@src/lib/icons/18px/grid";
import GraduationCapOutlineIcon from "@src/lib/icons/18px/graduation-cap-outline";
import GearOutlineIcon from "@src/lib/icons/18px/gear-outline";
import HomeOutlineIcon from "@src/lib/icons/18px/home-outline";
import LanguageIcon from "@src/lib/icons/18px/language";
import LinkIcon from "@src/lib/icons/18px/link";
import OpieOutlineIcon from "@src/lib/icons/18px/opie-outline";
import PinsOutlineIcon from "@src/lib/icons/18px/pins-outline";
import WindowChartLineOutlineIcon from "@src/lib/icons/18px/window-chart-line-outline";
import ShieldCheckOutlineIcon from "@src/lib/icons/18px/shield-check-outline";
import ShopOutlineIcon from "@src/lib/icons/18px/shop-outline";
import Sitemap3OutlineIcon from "@src/lib/icons/18px/sitemap-3-outline";
import UsersOutlineIcon from "@src/lib/icons/18px/users-outline";
import UsersSettingsOutlineIcon from "@src/lib/icons/18px/users-settings-outline";
import IdBadge2OutlineIcon from "@src/lib/icons/18px/id-badge-2-outline";
import SquareDashedSearchSparkleIcon from "@src/lib/icons/18px/square-dashed-search-sparkle";
import SuitcaseOutlineIcon from "@src/lib/icons/18px/suitcase-outline";
import TagOutlineIcon from "@src/lib/icons/18px/tag-outline";
import FileTextOutlineIcon from "@src/lib/icons/18px/file-text-outline";
import StudentIcon from "@src/lib/icons/18px/student";
import UiCardOutline18px from "@src/lib/icons/18px/ui-card-outline";
import WorkflowIcon from "@src/lib/icons/18px/workflow";
import { LibraryIcon } from "lucide-react";

export type AdminNavSectionId =
  | "home"
  | "reporting"
  | "people"
  | "training"
  | "operations"
  | "recommendations"
  | "messages"
  | "newsfeed"
  | "assignments"
  | "library"
  | "settings"
  | "accountPlan";

export type AdminNavIcon = React.ComponentType<{ className?: string }>;

export type AdminSecondaryNavItem = {
  label: string;
  href?: string;
  Icon: AdminNavIcon;
  /** Shown at the right edge, e.g. an arrow for a link out of the section. */
  TrailingIcon?: AdminNavIcon;
  /** Leaves this item out of the jump-to bar, e.g. when it already lists the route. */
  hideFromJumpTo?: boolean;
  disabled?: boolean;
  comingSoon?: boolean;
  locationGroupType?: LocationGroupType;
  /** Adds top spacing before this item to visually separate nav sections. */
  sectionBreakBefore?: boolean;
  isActive?: (pathname: string) => boolean;
  isVisible?: (context: AdminNavVisibilityContext) => boolean;
};

export type AdminPrimaryNavItem = {
  id: AdminNavSectionId;
  label: string;
  href?: string;
  Icon: AdminNavIcon;
  items: AdminSecondaryNavItem[];
  isActive: (pathname: string) => boolean;
  isVisible?: (context: AdminNavVisibilityContext) => boolean;
};

export type AdminNavVisibilityContext = {
  permissions?: UserActionPermissionBooleans | null;
  featureFlags: Partial<Record<Ff, boolean>>;
  billingInitialized?: boolean;
  userType?: UserType | null;
};

const TASK_LISTS_NAV_HREF = "/library/forms?type=task-lists";

const hasOperationsProduct = ({
  featureFlags,
}: AdminNavVisibilityContext): boolean =>
  Boolean(featureFlags[Ff.Audits] || featureFlags[Ff.TaskLibraryItemType]);

const exact = (href: string) => (pathname: string) => pathname === href;
const startsWith = (href: string) => (pathname: string) =>
  pathname === href || pathname.startsWith(`${href}/`);
const isLibraryFormsPath = startsWith("/library/forms");

const isLocationDetailPath = (pathname: string): boolean =>
  pathname === "/locations" || pathname.startsWith("/locations/[id]");

export const messagesPrimaryNavItem: AdminPrimaryNavItem = {
  id: "messages",
  label: "Messages",
  href: "/messages",
  Icon: EnvelopeContentOutlineIcon,
  isActive: startsWith("/messages"),
  items: [
    {
      label: "Preferences",
      href: "/messages/preferences",
      Icon: SlidersOutlineIcon,
      isActive: startsWith("/messages/preferences"),
      isVisible: ({ featureFlags }) => Boolean(featureFlags[Ff.NewMessaging]),
    },
    {
      label: "Archive",
      href: "/messages/archived-chats",
      Icon: ArchiveOutlineIcon,
      isActive: startsWith("/messages/archived-chats"),
      isVisible: ({ featureFlags }) => Boolean(featureFlags[Ff.NewMessaging]),
    },
  ],
};

export const newsfeedPrimaryNavItem: AdminPrimaryNavItem = {
  id: "newsfeed",
  label: "Newsfeed",
  href: "/newsfeed",
  Icon: NewspaperOutlineIcon,
  isActive: startsWith("/newsfeed"),
  isVisible: ({ featureFlags }) => Boolean(featureFlags[Ff.NewMessaging]),
  items: [
    {
      label: "Activity",
      href: "/newsfeed/activity",
      Icon: Archive2OutlineIcon,
      isActive: startsWith("/newsfeed/activity"),
      isVisible: ({ featureFlags }) => Boolean(featureFlags[Ff.NewMessaging]),
    },
    {
      label: "Posts",
      href: "/newsfeed",
      Icon: UiCardOutline18px,
      isActive: exact("/newsfeed"),
      isVisible: ({ featureFlags }) => Boolean(featureFlags[Ff.NewMessaging]),
    },
    {
      label: "Reported Content",
      href: "/newsfeed/reported-content",
      Icon: Flag3FillIcon,
      isActive: startsWith("/newsfeed/reported-content"),
      isVisible: ({ featureFlags, userType }) =>
        Boolean(featureFlags[Ff.NewMessaging] && userType === UserType.Admin),
    },
    {
      label: "View engagement",
      href: "/reporting/message-reads-grid",
      Icon: GridIcon,
      TrailingIcon: ArrowUpRightIcon,
      // Jump-to already has "Reporting > Newsfeed Engagement" for this route.
      hideFromJumpTo: true,
      isVisible: ({ featureFlags, permissions }) =>
        Boolean(featureFlags[Ff.NewMessaging] && permissions?.viewReporting),
    },
    {
      label: "Preferences",
      href: "/newsfeed/preferences",
      Icon: SlidersOutlineIcon,
      isActive: startsWith("/newsfeed/preferences"),
      isVisible: ({ featureFlags }) => Boolean(featureFlags[Ff.NewMessaging]),
    },
  ],
};

export const adminPrimaryNavItems: AdminPrimaryNavItem[] = [
  {
    id: "home",
    label: "Home",
    href: "/",
    Icon: HomeOutlineIcon,
    isActive: exact("/"),
    items: [],
  },
  {
    id: "training",
    label: "Training",
    href: "/library",
    Icon: GraduationCapOutlineIcon,
    isActive: (pathname) =>
      (startsWith("/library")(pathname) && !isLibraryFormsPath(pathname)) ||
      startsWith("/paths")(pathname) ||
      startsWith("/workflows")(pathname) ||
      startsWith("/automations")(pathname) ||
      startsWith("/access")(pathname) ||
      startsWith("/knowledge-check")(pathname) ||
      startsWith("/marketplace")(pathname) ||
      exact("/settings/archive")(pathname),
    items: [
      {
        label: "Library",
        href: "/library",
        Icon: BookOpenOutlineIcon,
        isActive: startsWith("/library"),
      },
      {
        label: "Marketplace",
        href: "/marketplace",
        Icon: ShopOutlineIcon,
        isActive: exact("/marketplace"),
      },
      {
        label: "Paths",
        href: "/paths",
        Icon: AsteriskIcon,
        isActive: startsWith("/paths"),
        isVisible: ({ permissions, featureFlags }) =>
          !featureFlags[Ff.PathsAsWorkflows] ||
          Boolean(permissions?.manageLibrary && permissions.updateAutomations),
      },
      {
        label: "Workflows",
        href: "/workflows",
        Icon: WorkflowIcon,
        isActive: startsWith("/workflows"),
        isVisible: ({ permissions, featureFlags }) =>
          Boolean(
            featureFlags[Ff.WorkflowsTab] &&
              permissions?.manageLibrary &&
              permissions.updateAutomations,
          ),
      },
      {
        label: "Automations",
        href: "/automations",
        Icon: BoltOutlineIcon,
        isActive: startsWith("/automations"),
        isVisible: ({ permissions }) =>
          Boolean(permissions?.manageLibrary && permissions.updateAutomations),
      },
      {
        label: "Access",
        href: "/access",
        Icon: FileSendOutlineIcon,
        isActive: startsWith("/access"),
        isVisible: ({ permissions }) =>
          Boolean(permissions?.manageLibrary && permissions.updateAutomations),
      },
      {
        label: "Knowledge Check",
        href: "/knowledge-check",
        Icon: BoltSpeed2Icon,
        isActive: startsWith("/knowledge-check"),
        isVisible: ({ permissions }) => Boolean(permissions?.manageLibrary),
      },
      {
        label: "Archive",
        href: "/settings/archive",
        Icon: ArchiveOutlineIcon,
        isActive: exact("/settings/archive"),
        isVisible: ({ permissions }) => Boolean(permissions?.createContent),
      },
    ],
  },
  {
    id: "operations",
    label: "Operations",
    href: "/library/forms",
    Icon: ClipboardIcon,
    isActive: (pathname) =>
      startsWith("/library/forms")(pathname) ||
      startsWith("/tasks")(pathname) ||
      startsWith("/forms")(pathname),
    items: [
      {
        // Hidden when both product flags are off so the secondary rail
        // collapses onto the Operations primary item (bundle upsell).
        label: "Checklists",
        href: TASK_LISTS_NAV_HREF,
        Icon: Tasks2Icon,
        isActive: startsWith("/library/forms"),
        isVisible: hasOperationsProduct,
      },
      {
        // Hide the Audits/Tasks upsell tabs unless the org already has a
        // product. Routes still render marketing pages if reached directly.
        label: "Audits",
        href: "/library/forms?type=audits",
        Icon: LawShieldIcon,
        isActive: startsWith("/library/forms"),
        isVisible: hasOperationsProduct,
      },
      {
        label: "Tasks",
        href: "/tasks",
        Icon: ListCheckboxIcon,
        isActive: startsWith("/tasks"),
        isVisible: hasOperationsProduct,
      },
    ],
  },
  {
    id: "recommendations",
    label: "Recommendations",
    href: "/recommendations",
    Icon: SquareDashedSearchSparkleIcon,
    isActive: (pathname) =>
      startsWith("/recommendations")(pathname) ||
      startsWith("/guest-feedback")(pathname),
    // The page itself requires `manageOrgIntegrations`
    // (pages/recommendations/index.tsx), which managers do not get by default.
    // Gate the nav on the permission too, or the link 404s. See Page.tsx.
    isVisible: ({ featureFlags, permissions }) =>
      Boolean(
        featureFlags[Ff.GuestFeedbackRecommendations] &&
          permissions?.manageOrgIntegrations,
      ),
    items: [],
  },
  {
    id: "reporting",
    label: "Reporting",
    href: "/reporting",
    Icon: WindowChartLineOutlineIcon,
    isActive: startsWith("/reporting"),
    isVisible: ({ permissions }) => Boolean(permissions?.viewReporting),
    items: [],
  },
  {
    id: "people",
    label: "People",
    href: "/users",
    Icon: UsersOutlineIcon,
    isActive: (pathname) =>
      startsWith("/locations")(pathname) ||
      startsWith("/roles")(pathname) ||
      startsWith("/users")(pathname) ||
      startsWith("/settings/hris")(pathname),
    items: [
      {
        label: "Brands",
        href: "/locations/brands",
        Icon: CopyrightOutlineIcon,
        isActive: startsWith("/locations/brands"),
        locationGroupType: LocationGroupType.Brand,
      },
      {
        label: "Regions",
        href: "/locations/regions",
        Icon: GlobeIcon,
        isActive: startsWith("/locations/regions"),
        locationGroupType: LocationGroupType.Region,
      },
      {
        label: "Franchises",
        href: "/locations/franchises",
        Icon: SuitcaseOutlineIcon,
        isActive: startsWith("/locations/franchises"),
        locationGroupType: LocationGroupType.Franchise,
      },
      {
        label: "Locations",
        href: "/locations",
        Icon: PinsOutlineIcon,
        isActive: isLocationDetailPath,
      },
      {
        label: "Departments",
        href: "/roles/departments",
        Icon: Sitemap3OutlineIcon,
        isActive: startsWith("/roles/departments"),
      },
      {
        label: "Roles",
        href: "/roles",
        Icon: IdBadge2OutlineIcon,
        isActive: (pathname) =>
          startsWith("/roles")(pathname) &&
          !startsWith("/roles/departments")(pathname),
      },
      {
        label: "Users",
        href: "/users",
        Icon: UsersOutlineIcon,
        isActive: startsWith("/users"),
      },
      {
        label: "HRIS",
        href: "/settings/hris",
        Icon: UsersSettingsOutlineIcon,
        isActive: startsWith("/settings/hris"),
        sectionBreakBefore: true,
        isVisible: ({ permissions }) =>
          Boolean(permissions?.manageOrgIntegrations),
      },
    ],
  },
  newsfeedPrimaryNavItem,
  messagesPrimaryNavItem,
];

export const traineePrimaryNavItems: AdminPrimaryNavItem[] = [
  {
    id: "assignments",
    label: "Your assignments",
    href: "/",
    Icon: StudentIcon,
    isActive: exact("/"),
    items: [],
  },
  {
    id: "library",
    label: "Library",
    href: "/my-training/library",
    Icon: LibraryIcon,
    isActive: startsWith("/my-training/library"),
    items: [],
  },
  newsfeedPrimaryNavItem,
  messagesPrimaryNavItem,
];

export const adminAccountPlanNavItem: AdminPrimaryNavItem = {
  id: "accountPlan",
  label: "Account Plan",
  href: "/account-plan",
  Icon: FileTextOutlineIcon,
  isActive: exact("/account-plan"),
  items: [],
};

export const adminSettingsNavItem: AdminPrimaryNavItem = {
  id: "settings",
  label: "Settings",
  href: "/settings",
  Icon: GearOutlineIcon,
  isActive: (pathname) =>
    startsWith("/settings")(pathname) &&
    !startsWith("/settings/hris")(pathname) &&
    pathname !== "/settings/archive",
  items: [
    {
      label: "Ask Opus",
      href: "/settings/ask-opus",
      Icon: OpieOutlineIcon,
      isActive: (pathname) =>
        pathname === "/settings" || pathname === "/settings/ask-opus",
      isVisible: ({ permissions }) => Boolean(permissions?.changeOrgSettings),
    },
    {
      label: "AI connections",
      href: "/settings/ai-connections",
      Icon: LinkIcon,
      isActive: exact("/settings/ai-connections"),
      isVisible: ({ permissions, featureFlags, userType }) =>
        Boolean(
          featureFlags[Ff.McpConnector] &&
            userType === UserType.Admin &&
            permissions?.changeOrgSettings,
        ),
    },
    {
      label: "Brand",
      href: "/settings/brand",
      Icon: BucketPaintOutlineIcon,
      isActive: exact("/settings/brand"),
      isVisible: ({ permissions }) => Boolean(permissions?.changeOrgSettings),
    },
    {
      label: "Wage & Hour",
      href: "/settings/compliance",
      Icon: ShieldCheckOutlineIcon,
      isActive: startsWith("/settings/compliance"),
      isVisible: ({ permissions }) => Boolean(permissions?.changeOrgSettings),
    },
    {
      label: "Languages",
      href: "/settings/language",
      Icon: LanguageIcon,
      isActive: exact("/settings/language"),
      isVisible: ({ permissions }) => Boolean(permissions?.changeOrgSettings),
    },
    {
      label: "Tags",
      href: "/settings/tags",
      Icon: TagOutlineIcon,
      isActive: exact("/settings/tags"),
    },
    {
      label: "Billing",
      href: "/settings/billing",
      Icon: CoinsOutlineIcon,
      isActive: exact("/settings/billing"),
      isVisible: ({ billingInitialized, permissions }) =>
        Boolean(billingInitialized && permissions?.manageBilling),
    },
    {
      label: "Training Imports",
      href: "/settings/training-imports",
      Icon: FileArrowInIcon,
      isActive: exact("/settings/training-imports"),
      isVisible: ({ permissions }) =>
        Boolean(permissions?.manageOrgIntegrations),
    },
    {
      label: "Certificate Imports",
      href: "/settings/certificate-imports",
      Icon: AwardCertificateOutlineIcon,
      isActive: exact("/settings/certificate-imports"),
      isVisible: ({ permissions }) =>
        Boolean(permissions?.manageOrgIntegrations),
    },
    {
      label: "Scheduled Invites",
      href: "/settings/scheduled-invites",
      Icon: CalendarOutlineIcon,
      isActive: exact("/settings/scheduled-invites"),
      // Scheduled invites is admin-only at the backend (UserActor + resolver
      // reject non-admins) even though `inviteOthers` is on by default for
      // managers. Gate on both or the page 404s for every manager.
      isVisible: ({ permissions, userType }) =>
        Boolean(permissions?.inviteOthers && userType === UserType.Admin),
    },
  ],
};

/**
 * Where the Settings rail entry should land for this user: the first settings
 * page they can open. `/settings` itself renders Ask Opus, which requires
 * `changeOrgSettings`, so linking there 404s for managers without it. Returns
 * null when no settings page is visible, in which case Settings is hidden.
 */
export const getSettingsLandingHref = (
  context: AdminNavVisibilityContext,
): string | null =>
  getVisibleSecondaryItems(adminSettingsNavItem.items, context)[0]?.href ??
  null;

export const getVisibleSecondaryItems = (
  items: AdminSecondaryNavItem[],
  context: AdminNavVisibilityContext,
): AdminSecondaryNavItem[] =>
  items.filter((item) => item.isVisible?.(context) ?? true);

export const getVisiblePrimaryItems = (
  context: AdminNavVisibilityContext,
  items: AdminPrimaryNavItem[] = adminPrimaryNavItems,
): AdminPrimaryNavItem[] => {
  return items
    .filter((item) => item.isVisible?.(context) ?? true)
    .map((item) =>
      item.id === "messages" && context.featureFlags[Ff.NewMessaging]
        ? { ...item, label: "Messages", Icon: MsgsOutlineIcon }
        : item,
    );
};

export const findActivePrimaryItem = (
  pathname: string,
  items: AdminPrimaryNavItem[],
): AdminPrimaryNavItem => {
  return (
    items.find((item) => item.isActive(pathname)) ||
    (adminSettingsNavItem.isActive(pathname)
      ? adminSettingsNavItem
      : adminAccountPlanNavItem.isActive(pathname)
        ? adminAccountPlanNavItem
        : items[0])
  );
};
